import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FiberNew as FiberNewIcon,
  Groups as GroupsIcon,
  HowToReg as HowToRegIcon,
  InfoOutlined as InfoOutlinedIcon,
  LocalOffer as LocalOfferIcon,
  Phone as PhoneIcon,
  Upcoming as UpcomingIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  AnticipatePricePromo,
  AnticipatePricePromoInput,
  AnticipatePricePromoInterest,
  AnticipatePricePromoInterestInput,
} from '../../services/api';

const DESCRIPTION_HINT = 'e.g. High floor with pool view, Near to prestige school';

/** Shown under the list to explain the feature. */
const ANTICIPATE_EXPLANATION = (
  <>
    Anticipate Price Promos are upcoming <strong>Possible</strong> promos announced by the project team before they go
    live. Register your interest and the project team will reach out to you first when the promo lands. Promo price may
    or may not happen. T&amp;C applies
  </>
);

const formatPrice = (value: number): string => `$${Math.round(value).toLocaleString()}`;

/** Formats the price field as the user types: digits only, thousands separators. */
const formatPriceInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  return digits ? Number(digits).toLocaleString('en-US') : '';
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Manager-facing title. The API only reveals the project to its managers
 * (project head / admin); agents see no project reference at all — the promo
 * leads with its price and description.
 */
const promoTitle = (promo: AnticipatePricePromo): string => promo.projectName ?? promo.projectId ?? '';

interface PromoFormState {
  projectId: string;
  price: string;
  description: string;
}

const EMPTY_PROMO_FORM: PromoFormState = { projectId: '', price: '', description: '' };

export interface AnticipatePricePromoTabProps {
  items: AnticipatePricePromo[];
  loading: boolean;
  error: string | null;
  /** True when the current user (project head / admin) may create promos. */
  canCreate: boolean;
  manageableProjects: Array<{ projectId: string; projectName: string }>;
  /** Promo ids the agent hasn't seen yet — rendered with a NEW pulse. */
  unseenIds: Set<number>;
  /** Prefill for the register-interest form (current agent's profile). */
  defaultInterest: AnticipatePricePromoInterestInput;
  onCreate: (input: AnticipatePricePromoInput) => Promise<void>;
  onUpdate: (id: number, input: { price: number; description: string }) => Promise<void>;
  onTakeDown: (id: number) => Promise<void>;
  onRegisterInterest: (id: number, input: AnticipatePricePromoInterestInput) => Promise<void>;
  onFetchInterests: (id: number) => Promise<AnticipatePricePromoInterest[]>;
  /** Fired once the agent is looking at this tab — clears the "new" nudges. */
  onViewed: () => void;
}

/**
 * "Anticipate Price Promo" tab of the Price Promo Watchlist dialog: upcoming
 * promos teased by project heads. Everyone can browse and register interest
 * (CEA / name / mobile, prefilled from their profile); the posting head or an
 * admin can create, edit, take down and see who registered.
 */
const AnticipatePricePromoTab: React.FC<AnticipatePricePromoTabProps> = ({
  items,
  loading,
  error,
  canCreate,
  manageableProjects,
  unseenIds,
  defaultInterest,
  onCreate,
  onUpdate,
  onTakeDown,
  onRegisterInterest,
  onFetchInterests,
  onViewed,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // "NEW" chips stay visible while the agent browses, even though opening the
  // tab immediately marks everything seen (clearing Linus / sidebar nudges).
  const [highlightIds, setHighlightIds] = useState<Set<number>>(() => new Set(unseenIds));
  useEffect(() => {
    setHighlightIds((prev) => {
      if (Array.from(unseenIds).every((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      unseenIds.forEach((id) => next.add(id));
      return next;
    });
  }, [unseenIds]);
  useEffect(() => {
    if (!loading) onViewed();
  }, [loading, items, onViewed]);

  const selected = selectedId === null ? null : items.find((p) => p.id === selectedId) ?? null;

  // ── Create / edit form ────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [formTarget, setFormTarget] = useState<AnticipatePricePromo | null>(null);
  const [form, setForm] = useState<PromoFormState>(EMPTY_PROMO_FORM);
  const [formSaving, setFormSaving] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [takeDownArmed, setTakeDownArmed] = useState<boolean>(false);

  const openCreateForm = () => {
    setFormTarget(null);
    setForm({
      ...EMPTY_PROMO_FORM,
      projectId: manageableProjects.length === 1 ? manageableProjects[0].projectId : '',
    });
    setFormError(null);
    setTakeDownArmed(false);
    setFormOpen(true);
  };

  const openEditForm = (promo: AnticipatePricePromo) => {
    setFormTarget(promo);
    setForm({
      projectId: promo.projectId ?? '',
      price: formatPriceInput(String(Math.round(promo.price))),
      description: promo.description,
    });
    setFormError(null);
    setTakeDownArmed(false);
    setFormOpen(true);
  };

  const handleFormSave = async () => {
    const price = Number(form.price.replace(/[,$\s]/g, ''));
    const description = form.description.trim();
    if (!formTarget && !form.projectId) { setFormError('Please select the project'); return; }
    if (!Number.isFinite(price) || price <= 0) { setFormError('Please enter a valid price'); return; }
    if (!description) { setFormError('Please enter a description'); return; }
    try {
      setFormSaving(true);
      setFormError(null);
      if (formTarget) {
        await onUpdate(formTarget.id, { price, description });
      } else {
        await onCreate({ projectId: form.projectId, price, description });
      }
      setFormOpen(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save anticipate price promo');
    } finally {
      setFormSaving(false);
    }
  };

  const handleTakeDown = async () => {
    if (!formTarget) return;
    if (!takeDownArmed) {
      setTakeDownArmed(true);
      return;
    }
    try {
      setFormSaving(true);
      setFormError(null);
      await onTakeDown(formTarget.id);
      setFormOpen(false);
      if (selectedId === formTarget.id) setSelectedId(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to take down anticipate price promo');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Registered interests (project head / admin) ───────────────────────────
  const [interests, setInterests] = useState<AnticipatePricePromoInterest[] | null>(null);
  const [interestsLoading, setInterestsLoading] = useState<boolean>(false);
  const [interestsError, setInterestsError] = useState<string | null>(null);

  const loadInterests = useCallback(async (id: number) => {
    try {
      setInterestsLoading(true);
      setInterestsError(null);
      setInterests(await onFetchInterests(id));
    } catch (err) {
      setInterestsError(err instanceof Error ? err.message : 'Failed to fetch registered interests');
    } finally {
      setInterestsLoading(false);
    }
  }, [onFetchInterests]);

  // ── Register interest ─────────────────────────────────────────────────────
  const [interestForm, setInterestForm] = useState<AnticipatePricePromoInterestInput>(defaultInterest);
  const [interestFormOpen, setInterestFormOpen] = useState<boolean>(false);
  const [interestSaving, setInterestSaving] = useState<boolean>(false);
  const [interestError, setInterestError] = useState<string | null>(null);
  const [justRegisteredId, setJustRegisteredId] = useState<number | null>(null);

  const openPromo = (promo: AnticipatePricePromo) => {
    setSelectedId(promo.id);
    setInterestForm(defaultInterest);
    setInterestFormOpen(!promo.myInterestRegistered);
    setInterestError(null);
    setJustRegisteredId(null);
  };

  const handleRegisterInterest = async () => {
    if (!selected) return;
    const cea = interestForm.cea.trim();
    const name = interestForm.name.trim();
    const mobile = interestForm.mobile.trim();
    if (!cea) { setInterestError('Please enter your CEA number'); return; }
    if (!name) { setInterestError('Please enter your name'); return; }
    if (!mobile) { setInterestError('Please enter your mobile number'); return; }
    try {
      setInterestSaving(true);
      setInterestError(null);
      await onRegisterInterest(selected.id, { cea, name, mobile });
      setJustRegisteredId(selected.id);
      setInterestFormOpen(false);
      if (selected.canManage) void loadInterests(selected.id);
    } catch (err) {
      setInterestError(err instanceof Error ? err.message : 'Failed to register interest');
    } finally {
      setInterestSaving(false);
    }
  };

  useEffect(() => {
    setInterests(null);
    setInterestsError(null);
    if (selected?.canManage) void loadInterests(selected.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // ── Renderers ─────────────────────────────────────────────────────────────

  const renderPromoCard = (promo: AnticipatePricePromo) => {
    const isNew = highlightIds.has(promo.id);
    return (
      <Card
        key={promo.id}
        variant="outlined"
        onClick={() => openPromo(promo)}
        sx={{
          position: 'relative',
          borderRadius: 2,
          border: isNew ? '2px solid #f44336' : '1px solid rgba(var(--color-text-rgb), 0.12)',
          borderLeft: isNew ? '4px solid #f44336' : '4px solid var(--color-accent)',
          backgroundColor: 'rgba(var(--color-shadow-rgb), 0.25)',
          boxShadow: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: isNew ? '#f44336' : 'var(--color-info-navy)',
            backgroundColor: 'rgba(var(--color-sheet-rgb), 0.35)',
            transform: 'translateY(-1px)',
          },
          ...(isNew
            ? {
                animation: 'anticipate-new-pulse 1.2s ease-in-out infinite',
                '@keyframes anticipate-new-pulse': {
                  '0%, 100%': { backgroundColor: 'rgba(244, 67, 54, 0.06)' },
                  '50%': { backgroundColor: 'rgba(244, 67, 54, 0.22)' },
                },
              }
            : {}),
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <UpcomingIcon sx={{ fontSize: 18, color: 'var(--color-accent)' }} />
            {promo.canManage ? (
              <>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'var(--color-text-strong)', minWidth: 0 }}>
                  {promoTitle(promo)}
                </Typography>
                <Tooltip title="Agents don't see the project name">
                  <VisibilityOffIcon sx={{ fontSize: 14, color: 'rgba(var(--color-text-rgb), 0.45)' }} />
                </Tooltip>
              </>
            ) : (
              <Typography sx={{ color: 'var(--color-success)', fontWeight: 800, fontSize: '1.05rem' }}>
                {formatPrice(promo.price)}
              </Typography>
            )}
            {isNew && (
              <Chip
                icon={<FiberNewIcon sx={{ fontSize: 16 }} />}
                label="NEW"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 800,
                  bgcolor: '#f44336',
                  color: '#fff',
                  animation: 'anticipate-badge-pulse 1.2s ease-in-out infinite',
                  '@keyframes anticipate-badge-pulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' },
                    '50%': { boxShadow: '0 0 0 6px rgba(244, 67, 54, 0)' },
                  },
                }}
              />
            )}
            <Box sx={{ flex: 1 }} />
            {promo.canManage && (
              <Tooltip title="Edit anticipate price promo">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditForm(promo);
                  }}
                  sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', '&:hover': { color: 'var(--color-accent)' } }}
                  aria-label={`Edit anticipate price promo for ${promoTitle(promo)}`}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {promo.canManage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
              <LocalOfferIcon sx={{ fontSize: 15, color: 'var(--color-success)' }} />
              <Typography sx={{ color: 'var(--color-success)', fontWeight: 800, fontSize: '1.05rem' }}>
                {formatPrice(promo.price)}
              </Typography>
            </Box>
          )}

          <Typography
            variant="body2"
            sx={{
              mt: 0.25,
              color: 'rgba(var(--color-text-rgb), 0.8)',
              fontStyle: 'italic',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            “{promo.description}”
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>
              Posted {formatDate(promo.createdAt)}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {promo.myInterestRegistered && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label="Registered"
                size="small"
                sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(var(--color-success-rgb, 76, 175, 80), 0.15)', color: 'var(--color-success)' }}
              />
            )}
            <Chip
              icon={<GroupsIcon sx={{ fontSize: 14 }} />}
              label={`${promo.interestCount} interested`}
              size="small"
              sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(var(--color-accent-rgb), 0.12)', color: 'var(--color-accent)' }}
            />
          </Box>
        </Box>
      </Card>
    );
  };

  const renderDetail = (promo: AnticipatePricePromo) => (
    <Stack spacing={2} sx={{ mt: 0.5 }}>
      {/* Back row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => setSelectedId(null)} sx={{ color: 'var(--color-text-strong)' }} aria-label="Back to anticipate price promos">
          <ArrowBackIcon />
        </IconButton>
        {promo.canManage && (
          <>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'var(--color-text-strong)', minWidth: 0 }} noWrap>
              {promoTitle(promo)}
            </Typography>
            <Tooltip title="Agents don't see the project name">
              <VisibilityOffIcon sx={{ fontSize: 15, color: 'rgba(var(--color-text-rgb), 0.45)' }} />
            </Tooltip>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {promo.canManage && (
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => openEditForm(promo)}
            sx={{ color: 'var(--color-accent)', textTransform: 'none', fontWeight: 700 }}
          >
            Edit
          </Button>
        )}
      </Box>

      {/* Hero */}
      <Box
        sx={{
          borderRadius: 2.5,
          p: 2,
          textAlign: 'center',
          border: '1px solid rgba(var(--color-accent-rgb), 0.35)',
          background:
            'linear-gradient(160deg, rgba(var(--color-accent-rgb), 0.16) 0%, rgba(var(--color-shadow-rgb), 0.25) 60%)',
        }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <AutoAwesomeIcon
            sx={{
              fontSize: 16,
              color: 'var(--color-accent)',
              animation: 'anticipate-sparkle 1.6s ease-in-out infinite',
              '@keyframes anticipate-sparkle': {
                '0%, 100%': { opacity: 1, transform: 'scale(1) rotate(0deg)' },
                '50%': { opacity: 0.65, transform: 'scale(1.25) rotate(18deg)' },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: 'var(--color-accent)', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>
            Anticipated Price
          </Typography>
        </Box>
        <Typography sx={{ color: 'var(--color-success)', fontWeight: 900, fontSize: { xs: '1.9rem', sm: '2.3rem' }, lineHeight: 1.1 }}>
          {formatPrice(promo.price)}
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, color: 'rgba(var(--color-text-rgb), 0.9)', fontStyle: 'italic' }}>
          “{promo.description}”
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(var(--color-text-rgb), 0.55)' }}>
          Posted {formatDate(promo.createdAt)}
          {promo.createdByName ? ` by ${promo.createdByName}` : ''} · {promo.interestCount} interested
        </Typography>
      </Box>

      {/* Register interest */}
      <Box sx={{ borderRadius: 2.5, border: '1px solid rgba(var(--color-text-rgb), 0.12)', p: 1.75 }}>
        {promo.myInterestRegistered && !interestFormOpen ? (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon
              sx={{
                fontSize: 40,
                color: 'var(--color-success)',
                ...(justRegisteredId === promo.id
                  ? {
                      animation: 'anticipate-registered-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
                      '@keyframes anticipate-registered-pop': {
                        '0%': { transform: 'scale(0.4)', opacity: 0 },
                        '100%': { transform: 'scale(1)', opacity: 1 },
                      },
                    }
                  : {}),
              }}
            />
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'var(--color-text-strong)' }}>
              You're on the list!
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', mb: 1 }}>
              The project team will contact you first when this promo goes live.
            </Typography>
            <Button
              size="small"
              onClick={() => {
                setInterestForm(defaultInterest);
                setInterestError(null);
                setInterestFormOpen(true);
              }}
              sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', textTransform: 'none', fontSize: '0.75rem' }}
            >
              Update my details
            </Button>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <HowToRegIcon sx={{ color: 'var(--color-accent)' }} />
              <Typography variant="subtitle2" fontWeight={800} sx={{ color: 'var(--color-text-strong)' }}>
                Register Interest
              </Typography>
            </Box>
            {interestError && <Alert severity="error">{interestError}</Alert>}
            <TextField
              label="CEA Number"
              value={interestForm.cea}
              onChange={(e) => setInterestForm((f) => ({ ...f, cea: e.target.value }))}
              size="small"
              fullWidth
              required
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              label="Name"
              value={interestForm.name}
              onChange={(e) => setInterestForm((f) => ({ ...f, name: e.target.value }))}
              size="small"
              fullWidth
              required
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Mobile"
              value={interestForm.mobile}
              onChange={(e) => setInterestForm((f) => ({ ...f, mobile: e.target.value }))}
              size="small"
              fullWidth
              required
              type="tel"
              inputProps={{ maxLength: 20 }}
            />
            <Button
              onClick={handleRegisterInterest}
              disabled={interestSaving}
              variant="contained"
              startIcon={<HowToRegIcon />}
              sx={{
                bgcolor: 'var(--color-accent)',
                color: '#fff',
                fontWeight: 800,
                textTransform: 'none',
                py: 1,
                borderRadius: 2,
                boxShadow: '0 8px 20px rgba(var(--color-accent-rgb), 0.35)',
                '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.08)' },
              }}
            >
              {interestSaving ? 'Registering…' : promo.myInterestRegistered ? 'Update My Interest' : 'Register Interest'}
            </Button>
          </Stack>
        )}
      </Box>

      {/* Registered interests — project head / admin only */}
      {promo.canManage && (
        <Box sx={{ borderRadius: 2.5, border: '1px solid rgba(var(--color-text-rgb), 0.12)', p: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <GroupsIcon sx={{ color: 'var(--color-accent)' }} />
            <Typography variant="subtitle2" fontWeight={800} sx={{ color: 'var(--color-text-strong)', flex: 1 }}>
              Registered Interests{interests ? ` (${interests.length})` : ''}
            </Typography>
            {interestsLoading && <CircularProgress size={16} />}
          </Box>
          {interestsError && <Alert severity="error" sx={{ mb: 1 }}>{interestsError}</Alert>}
          {interests && interests.length === 0 && (
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
              No one has registered interest yet.
            </Typography>
          )}
          {interests && interests.length > 0 && (
            <Stack divider={<Divider sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.08)' }} />} spacing={0.75}>
              {interests.map((row) => (
                <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: 'var(--color-text-strong)', minWidth: 0 }}>
                    {row.name}
                  </Typography>
                  <Chip label={row.cea} size="small" sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(var(--color-text-rgb), 0.1)', color: 'var(--color-text-strong)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 13, color: 'rgba(var(--color-text-rgb), 0.55)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                      {row.mobile}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>
                    {formatDate(row.createdAt)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );

  return (
    <>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {selected ? (
        renderDetail(selected)
      ) : (
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          {canCreate && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateForm}
              sx={{
                bgcolor: 'var(--color-accent)',
                color: '#fff',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                py: 1,
                '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.08)' },
              }}
            >
              Create Anticipate Price Promo
            </Button>
          )}

          {items.length === 0 && !loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <UpcomingIcon sx={{ fontSize: 40, color: 'rgba(var(--color-text-rgb), 0.25)', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                No anticipate price promos right now. Check back soon — upcoming price promos will be announced here first.
              </Typography>
            </Box>
          ) : (
            items.map(renderPromoCard)
          )}

          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', px: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'rgba(var(--color-text-rgb), 0.5)', mt: '1px' }} />
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
              {ANTICIPATE_EXPLANATION}
            </Typography>
          </Box>
        </Stack>
      )}

      {/* Create / edit form — project head / admin */}
      <Dialog
        open={formOpen}
        onClose={() => { if (!formSaving) setFormOpen(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'var(--color-surface)',
            color: 'rgba(var(--color-text-rgb), 0.9)',
            boxShadow: '0 4px 24px rgba(var(--color-shadow-rgb), 0.4)',
            border: '1px solid rgba(var(--color-text-rgb), 0.08)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--color-text-strong)', fontWeight: 600, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          {formTarget ? 'Edit Anticipate Price Promo' : 'Create Anticipate Price Promo'}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'var(--color-surface)', borderColor: 'rgba(var(--color-text-rgb), 0.08)' }}>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            {formTarget ? (
              <TextField
                label="Project"
                value={promoTitle(formTarget)}
                size="small"
                fullWidth
                disabled
              />
            ) : (
              <Autocomplete
                options={manageableProjects}
                getOptionLabel={(o) => o.projectName}
                isOptionEqualToValue={(o, v) => o.projectId === v.projectId}
                value={manageableProjects.find((p) => p.projectId === form.projectId) ?? null}
                onChange={(_, value) => setForm((f) => ({ ...f, projectId: value?.projectId ?? '' }))}
                size="small"
                renderInput={(params) => <TextField {...params} label="Project" required />}
              />
            )}
            <TextField
              label="Price (SGD)"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: formatPriceInput(e.target.value) }))}
              size="small"
              fullWidth
              required
              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              size="small"
              fullWidth
              required
              multiline
              minRows={2}
              placeholder={DESCRIPTION_HINT}
              helperText={DESCRIPTION_HINT}
              inputProps={{ maxLength: 500 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)', bgcolor: 'var(--color-surface)', px: 2, py: 1.5, gap: 1 }}>
          {formTarget && (
            <Button
              onClick={handleTakeDown}
              disabled={formSaving}
              startIcon={<DeleteIcon />}
              sx={{ color: '#f44336', textTransform: 'none', fontWeight: 700, mr: 'auto' }}
            >
              {takeDownArmed ? 'Confirm take down?' : 'Take down'}
            </Button>
          )}
          <Button onClick={() => setFormOpen(false)} disabled={formSaving} sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleFormSave}
            disabled={formSaving}
            variant="contained"
            sx={{
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.08)' },
            }}
          >
            {formSaving ? 'Saving…' : formTarget ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AnticipatePricePromoTab;
